import React, { useState } from 'react';
import { fmtDateLong } from '../../utils/cameraRelay';
import { C } from './CamerasStyles';
import { SnoozeForm } from './SecurityPill';
import {
  ALERT_FILTERS,
  OUTCOMES,
  STRIP_AXIS,
  alertTitle,
  clipTarget,
  groupAlertsByNight,
  outcomeLabel,
  pressSnooze,
  shopClock,
  stripLanes,
  tonightWindow,
} from '../../features/security/nightWatch';
import { useNightWatchAlerts, useNightWatchTimeline } from '../../features/security/useNightWatch';
import { isDoorAlert } from '../../features/security/doors';
import { DoorBadge } from './DoorAlarm';

// Cameras → Alerts (?mode=alerts): tonight as one strip (6 PM – 6 AM, across midnight: a 2 AM alert
// belongs to the evening before), then the night watch's incidents grouped by night, ranked by what
// happened to them (texted > unsure > held by Claude > let go > dropped). Rules: features/security.
// Door alerts (source 'door', 2026-10-25) sit in the same list: a door badge, the door's stacked photo
// (opens full size), its photo note, and Watch clip on the door's first camera.

const STAKE = { bad: C.bad, warn: C.warn, held: '#7d8693', letgo: 'transparent', dropped: 'transparent' };
export const OUTCOME_CHIP = {
  not_texted: { bg: '#5a1d1d', fg: '#ffd0d0' },
  texted: { bg: '#3a1717', fg: '#ffb0b0' },
  unsure: { bg: '#3a2c12', fg: '#ffd38a' },
  held: { bg: '#262b31', fg: '#c3cad2' },
  let_go: { bg: '#1d2a22', fg: '#a9e2c0' },
  dropped: { bg: '#1d2633', fg: '#b7d3f7' },
};
const BLOCK = {
  armed: { background: '#1c3557', color: '#cfe3ff' },
  off: { background: 'repeating-linear-gradient(135deg,#3a3326 0 6px,#332d22 6px 12px)', color: '#e8c98c' },
  offline: { background: 'repeating-linear-gradient(135deg,#4a1d1d 0 6px,#3a1717 6px 12px)', color: '#ffb0b0' },
  fallback: { background: '#3a2c12', color: '#ffd38a' },
  snoozed: { background: '#3a2c12', color: '#ffd38a' },
  present: { background: '#3a4048', color: '#dfe4ea' },
  ghost: { background: 'repeating-linear-gradient(90deg,#2a2f35 0 4px,transparent 4px 8px)' },
};
const TICK = { not_texted: C.bad, texted: C.bad, unsure: C.warn, held: '#7d8693', let_go: '#a9e2c0', dropped: '#b7d3f7', present: C.live, off: C.muted };

function Lane({ lane, nowPct }) {
  return (
    <>
      <span className="jqc-ell" style={{ fontSize: 12, color: C.muted }}>{lane.label}</span>
      <div style={{ position: 'relative', height: lane.key === 'watch' ? 24 : 16, background: C.track, borderRadius: 5, overflow: 'hidden' }}>
        {lane.blocks.map((b, i) => (
          <span key={i} title={b.title}
                style={{ position: 'absolute', top: 0, bottom: 0, left: `${b.left}%`, width: `${b.width}%`, fontSize: 11, fontWeight: 600,
                         padding: '0 6px', display: 'flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap', ...BLOCK[b.kind] }}>
            {b.label && b.width > 12 ? b.label : ''}
          </span>
        ))}
        {lane.ticks.map((t, i) => (
          <span key={`t${i}`} title={t.title}
                style={{ position: 'absolute', top: 2, bottom: 2, left: `${t.left}%`, width: 4, marginLeft: -2, borderRadius: 2, background: TICK[t.kind] || C.muted }} />
        ))}
        {nowPct != null && nowPct < 100 && (
          <>
            <span style={{ position: 'absolute', top: 0, bottom: 0, left: `${nowPct}%`, right: 0, background: 'rgba(17,19,22,.55)' }} />
            <span style={{ position: 'absolute', top: -4, bottom: -4, left: `${nowPct}%`, width: 2, marginLeft: -1, background: '#fff' }} />
          </>
        )}
      </div>
    </>
  );
}

export function TonightStrip({ status, active }) {
  const now = new Date();
  // Recomputed each render: the same night gives the same ISO strings, so the read re-runs only when
  // 6 AM / 6 PM passes with the page open.
  const w = tonightWindow(now);
  const { timeline, error } = useNightWatchTimeline(active, w.start.toISOString(), w.end.toISOString());
  const lanes = stripLanes(timeline, status, w, now);
  const nowPct = now < w.end ? Math.max(0, ((now - w.start) / (w.end - w.start)) * 100) : null;
  const tonight = now >= w.start && now < w.end;
  return (
    <section style={{ padding: '16px 24px 8px', display: 'grid', gap: 10 }} aria-label="Tonight">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 14px' }}>
        <b style={{ fontSize: 15 }}>{tonight ? 'Tonight' : 'Last night'} · {fmtDateLong(w.night)}</b>
        <span style={{ fontSize: 13, color: C.muted }}>6 PM – 6 AM</span>
        {error && <span style={{ fontSize: 12, color: C.bad }}>{error}</span>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(64px, 120px) 1fr', gap: '8px 12px', alignItems: 'center' }}>
        {lanes.map((l) => <Lane key={l.key} lane={l} nowPct={l.key === 'watch' || l.key.startsWith('p:') ? nowPct : null} />)}
        <span />
        <div style={{ position: 'relative', height: 16, fontSize: 11, color: '#7d8590' }}>
          {STRIP_AXIS.map((t, i) => (
            <span key={t} style={{ position: 'absolute', left: `${i * 25}%`, transform: i === 0 ? 'none' : i === 4 ? 'translateX(-100%)' : 'translateX(-50%)' }}>{t}</span>
          ))}
        </div>
      </div>
      <span style={{ fontSize: 12, color: C.muted }}>Dashed = how long that signal keeps counting if nothing changes.</span>
    </section>
  );
}

export function AlertRow({ a, cameras, onWatchClip, onLive, onSnoozeCamera, snoozeBusy }) {
  const [snoozing, setSnoozing] = useState(false);
  const [showBefore, setShowBefore] = useState(false);
  const meta = OUTCOMES[a.outcome] || {};
  const chip = OUTCOME_CHIP[a.outcome] || OUTCOME_CHIP.held;
  const camera = (cameras || []).find((c) => c.key === a.camera_key);
  const clip = clipTarget(a);
  const door = isDoorAlert(a);
  const photo = showBefore && a.before_url ? a.before_url : a.photo_url;
  const watchTitle = camera ? '' : door ? 'No camera is picked for this door' : 'This alert came before camera keys were recorded';
  return (
    <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(110px, 160px) minmax(0, 1fr)', gap: 14, alignItems: 'center',
                  padding: 10, border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface }}
         data-testid="alert-row" data-source={door ? 'door' : 'camera'}>
      <span style={{ position: 'absolute', left: -1, top: 10, bottom: 10, width: 3, borderRadius: 2, background: STAKE[meta.tone] || 'transparent' }} />
      <div style={{ position: 'relative', aspectRatio: '7 / 4', borderRadius: 7, overflow: 'hidden', background: '#0b0d0f' }}>
        {photo && door ? (
          <a href={photo} target="_blank" rel="noreferrer" title="Open the photo full size" style={{ display: 'block', width: '100%', height: '100%' }}>
            <img src={photo} alt={alertTitle(a)} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
          </a>
        ) : photo ? <img src={photo} alt={`${alertTitle(a)}${showBefore ? ' (before)' : ''}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          : <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: C.faint }}>no photo</span>}
        {a.before_url && (
          <button type="button" onClick={() => setShowBefore((b) => !b)} aria-pressed={showBefore}
                  style={{ position: 'absolute', left: 5, bottom: 4, fontSize: 10, color: '#cfd5db', background: 'rgba(0,0,0,.6)', border: 0, borderRadius: 3, padding: '1px 5px', cursor: 'pointer' }}>
            {showBefore ? `before · ${shopClock(a.before_at)}` : 'show before'}
          </button>
        )}
      </div>
      <div style={{ display: 'grid', gap: 5, minWidth: 0 }}>
        <b style={{ fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          {door && <DoorBadge />}
          {alertTitle(a)}
        </b>
        <span style={{ width: 'fit-content', fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: chip.bg, color: chip.fg }}>
          {outcomeLabel(a)}
        </span>
        {a.review?.text && <span style={{ fontSize: 12.5, color: C.textSoft }}><span style={{ color: C.faint }}>Claude:</span> {a.review.verdict} · {a.review.text}</span>}
        {a.errors?.length > 0 && !(door && a.outcome === 'not_texted') && <span style={{ fontSize: 12, color: C.warn }}>{a.errors.join('; ')}</span>}
        {door && a.cameras?.length > 0 && <span style={{ fontSize: 12, color: C.muted }}>Cameras: {a.cameras.join(', ')}</span>}
        {a.photo_note && <span style={{ fontSize: 11.5, color: C.faint }}>{a.photo_note}</span>}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button type="button" className="jqc-btn" disabled={!camera} title={watchTitle}
                  onClick={() => onWatchClip(camera, clip.day, clip.minute)}>
            ▶ Watch {shopClock(new Date(new Date(a.seen_at).getTime() - 30000))}
          </button>
          {camera && <button type="button" className="jqc-btn" onClick={() => onLive(camera.key)}>Live</button>}
          {camera && onSnoozeCamera && !door && (
            <button type="button" className="jqc-btn" aria-expanded={snoozing} onClick={() => setSnoozing((s) => !s)}>Snooze camera</button>
          )}
        </div>
        {snoozing && (
          <SnoozeForm cameras={cameras} cameraKey={a.camera_key} busy={snoozeBusy} onCancel={() => setSnoozing(false)}
                      onSubmit={async (f) => { if (await onSnoozeCamera(f)) setSnoozing(false); }} />
        )}
      </div>
    </div>
  );
}

export default function AlertsView({ cameras, status, ui, active = true, onWatchClip, onLive, onChanged }) {
  const [filter, setFilter] = useState('all');
  const [cameraKey, setCameraKey] = useState('');
  const [busy, setBusy] = useState(false);
  // 7 days back, on the hour: stable between polls, moves forward with the page left open.
  const since = new Date(Math.floor((Date.now() - 7 * 86400000) / 3600000) * 3600000).toISOString();
  const { alerts, error } = useNightWatchAlerts(active, since);
  const nights = groupAlertsByNight(alerts, filter, cameraKey || null);

  const snoozeCamera = async (f) => {
    setBusy(true);
    try {
      const r = await pressSnooze({ ui, ...f });
      if (r.ok && onChanged) onChanged();
      return !!r.ok;
    } finally {
      setBusy(false);
    }
  };

  return (
    <main style={{ paddingBottom: 28 }}>
      <TonightStrip status={status} active={active} />
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '10px 24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="group" aria-label="Show">
          {ALERT_FILTERS.map((f) => (
            <button key={f.key} type="button" className="jqc-chip" aria-pressed={filter === f.key} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>
        <span style={{ flex: 1 }} />
        <select aria-label="Camera" value={cameraKey} onChange={(e) => setCameraKey(e.target.value)}
                style={{ minHeight: 34, borderRadius: 8, border: `1px solid ${C.borderStrong}`, background: C.raised, color: C.text, padding: '0 8px' }}>
          <option value="">All cameras</option>
          {(cameras || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'grid', gap: 12, padding: '0 24px' }}>
        {error && <div style={{ color: C.bad, fontSize: 13 }}>{error}</div>}
        {alerts === null && !error && <div style={{ color: C.muted }}>Loading alerts…</div>}
        {alerts && !nights.length && <div style={{ color: C.faint, padding: 24, textAlign: 'center' }}>No night-watch alerts in the last 7 days{filter !== 'all' || cameraKey ? ' that match' : ''}.</div>}
        {nights.map((n) => (
          <React.Fragment key={n.night}>
            <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: C.faint, paddingTop: 6 }}>
              {fmtDateLong(n.night)} night
            </div>
            {n.alerts.map((a) => (
              <AlertRow key={isDoorAlert(a) ? `door-${a.event_id}` : `cam-${a.incident_id}`} a={a} cameras={cameras} onWatchClip={onWatchClip} onLive={onLive}
                        onSnoozeCamera={ui ? snoozeCamera : null} snoozeBusy={busy} />
            ))}
          </React.Fragment>
        ))}
      </div>
    </main>
  );
}
